import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import CategoryBadge from "@/components/CategoryBadge";
import Logo from "@/components/Logo";
import { PlusCircle, MapPin, Calendar, Wallet, Headset, Mail } from "lucide-react";
import { SUPPORT_EMAIL, CATEGORIES, CATEGORY_ICONS } from "@/lib/constants";
import { greet } from "@/lib/name";

export function StatusPill({ status, t }) {
  const map = {
    sent: "bg-sky-50 text-sky-700",
    offers_received: "bg-emerald-50 text-emerald-700",
    accepted: "bg-emerald-600 text-white",
    completed: "bg-zinc-100 text-zinc-700",
    expired: "bg-zinc-200 text-zinc-500",
    open: "bg-sky-50 text-sky-700",
  };
  return (
    <span data-testid={`status-pill-${status}`} className={`pill ${map[status] || "bg-zinc-100 text-zinc-700"}`}>
      {t(status)}
    </span>
  );
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const { t, tCat, tAct, tCity, lang } = useLang();
  const [requests, setRequests] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/requests/mine")
      .then(r => setRequests(r.data))
      .catch(e => setErr(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="app-shell pb-24" data-testid="customer-dashboard">
      <TopBar title={t("home")} back={false} />

      {/* Welcome hero */}
      <div className="px-5 pt-5">
        <div className="rounded-3xl band-deep text-white p-5 relative overflow-hidden rise">
          <div className="absolute -top-8 -end-8 h-32 w-32 rounded-full bg-white/5" />
          <div className="absolute -bottom-10 -start-6 h-28 w-28 rounded-full bg-white/5" />
          <p className="text-emerald-200/80 text-xs font-semibold">Hustlers KSA</p>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="font-display text-2xl font-extrabold" data-testid="customer-welcome-name">{greet(user, lang)}</h1>
          </div>
          <p className="text-emerald-100/70 text-sm mt-2">{t("matchingMerchants")}</p>
          <Link to="/customer/new" data-testid="new-request-btn" className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-white text-emerald-900 px-6 py-3 font-bold text-sm hover:bg-emerald-50 transition active:scale-[0.98] w-full sm:w-auto">
            <PlusCircle className="h-5 w-5" /> {t("newRequest")}
          </Link>
        </div>
      </div>

      {/* Quick categories */}
      <div className="px-5 mt-6 rise rise-1">
        <p className="label-caps mb-3">{t("whatDoYouNeed")}</p>
        <div className="grid grid-cols-5 gap-2">
          {CATEGORIES.slice(0, 10).map((c) => {
            const Icon = CATEGORY_ICONS[c];
            return (
              <Link key={c} to="/customer/new" data-testid={`quick-cat-${c}`} className="flex flex-col items-center gap-1.5 group">
                <div className="h-12 w-12 rounded-2xl bg-white border border-zinc-200 grid place-items-center text-emerald-700 shadow-sm group-hover:border-emerald-400 group-hover:shadow-md transition">
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <span className="text-[10px] text-zinc-600 text-center leading-tight">{tCat(c)}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent requests */}
      <div className="px-5 mt-8 rise rise-2">
        <div className="flex items-baseline justify-between">
          <h3 className="font-display text-lg font-extrabold text-[#12211D]">{t("myRecentRequests")}</h3>
          <Link to="/customer/requests" data-testid="view-all-requests" className="text-xs font-semibold text-emerald-700 hover:underline underline-offset-4">
            {t("viewAll")} ({requests.length})
          </Link>
        </div>

        {err && <div className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg p-3" role="alert">{err}</div>}
        {loading && <div className="mt-4 text-sm text-zinc-500">{t("loadingLabel")}</div>}
        {!loading && requests.length === 0 && (
          <div className="mt-4 card-surface p-5 text-sm text-zinc-600" data-testid="empty-state">{t("noRequests")}</div>
        )}

        <div className="mt-4 space-y-3">
          {requests.slice(0, 6).map((r) => (
            <Link
              key={r.id}
              to={`/customer/request/${r.id}`}
              data-testid={`request-card-${r.id}`}
              className="card-surface p-4 flex gap-3 items-start"
            >
              <CategoryBadge category={r.category} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-zinc-900 truncate">
                    {tCat(r.category)}{r.activity ? ` · ${tAct(r.activity)}` : ""}
                  </div>
                  <StatusPill status={r.effective_status || r.status} t={t} />
                </div>
                <p className="text-sm text-zinc-600 line-clamp-2 mt-0.5">{r.description}</p>
                <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(r.city)}{r.district ? ` — ${r.district}` : ""}</span>
                  <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{r.budget} {t("saudiRiyal")}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{r.deadline}</span>
                  <span className="ms-auto text-emerald-700 font-semibold">{r.offer_count || 0} {t("offers")}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Support card */}
      <div className="px-5 mt-8 rise rise-3">
        <div className="card-surface p-4 flex items-center gap-3" data-testid="support-card">
          <div className="h-11 w-11 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
            <Headset className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-zinc-900 text-sm">{t("supportTitle")}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{t("supportReady")}</div>
          </div>
          <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email-btn" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600 text-emerald-700 px-3.5 py-2 text-xs font-bold hover:bg-emerald-50 transition">
            <Mail className="h-3.5 w-3.5" /> {t("contactSupport")}
          </a>
        </div>
        <div className="mt-6 mb-2 flex justify-center opacity-60">
          <Logo className="h-6 w-auto" asLink={false} testId="customer-footer-logo" />
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
